import * as React from 'react';
import { useState, useEffect } from 'react';
import { branding } from '../constants/branding';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import axiosInstance from '../services/axiosConfig';
import type { DashboardState } from '../store/slices/dashboardSlice';
import { FaPiggyBank, FaUmbrellaBeach, FaCar, FaHome, FaPlane, FaGift } from 'react-icons/fa';

// Update types to match backend

type SavingsGoal = {
  id: number;
  name: string;
  targetAmount: number;
  currentAmount: number;
  progress: number;
  targetDate?: string;
  color?: string;
  icon?: string;
  monthlyContribution?: number;
};

type SavingsCategory = {
  id: number;
  name: string;
  value: number;
  change: string;
  color: string;
  icon: string;
  allocation: string;
  goals: SavingsGoal[];
};

type SavingsTransaction = {
  id: number;
  type: 'Deposit' | 'Withdrawal';
  category: string;
  amount: number;
  date: string;
  description: string;
  icon: string;
};

type SavingsPortfolio = {
  totalValue: string;
  totalChange: string;
  totalChangeValue: string;
  monthlyContribution: string;
  nextGoal: string;
  categories: SavingsCategory[];
  transactions: SavingsTransaction[];
};

const Savings: React.FC = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('1m');
  const [page, setPage] = useState(0);
  const pageSize = 5;

  const MOBILE_BREAKPOINT = 768;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);

  const [portfolio, setPortfolio] = useState<SavingsPortfolio | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.auth.token);
  const { data: dashboardData, isLoading: isDashboardLoading } = useAppSelector(state => state.dashboard as DashboardState);

  const [goals, setGoals] = useState<SavingsGoal[]>([]);
  const [selectedGoalId, setSelectedGoalId] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<SavingsTransaction[]>([]);

  const [showContributeModal, setShowContributeModal] = useState(false);
  const [contributeAmount, setContributeAmount] = useState('');
  const [contributeDescription, setContributeDescription] = useState('');
  const [contributeAccountId, setContributeAccountId] = useState('');
  const [isContributing, setIsContributing] = useState(false);

  const [showCreateGoalModal, setShowCreateGoalModal] = useState(false);
  const [newGoalName, setNewGoalName] = useState('');
  const [newGoalTarget, setNewGoalTarget] = useState('');
  const [isCreatingGoal, setIsCreatingGoal] = useState(false);

  // Add state for color and icon
  const [newGoalColor, setNewGoalColor] = useState('#1976d2');
  const [newGoalIcon, setNewGoalIcon] = useState('piggybank');

  // Add state for top up modal
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [topUpGoal, setTopUpGoal] = useState<SavingsGoal | null>(null);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpAccountId, setTopUpAccountId] = useState('');
  const [topUpDescription, setTopUpDescription] = useState('');
  const [isToppingUp, setIsToppingUp] = useState(false);

  // Add state for pagination
  const [goalsPage, setGoalsPage] = useState(0);
  const goalsPerPage = 6;
  const totalGoalsPages = Math.ceil(goals.length / goalsPerPage);
  const paginatedGoals = goals.slice(goalsPage * goalsPerPage, (goalsPage + 1) * goalsPerPage);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (token) {
      fetchPortfolio();
    } else {
      setIsLoading(false);
    }
  }, [token]);

  const fetchPortfolio = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get('/savings');
      if (response.status === 200 && response.data) {
        setPortfolio(response.data.data || response.data);
      }
    } catch (e) {
      setError('Failed to fetch savings portfolio');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch all goals
  const fetchGoals = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get('/savings-goals');
      if (response.status === 200 && response.data.data) {
        setGoals(response.data.data);
        // Auto-select first goal if none selected
        if (!selectedGoalId && response.data.data.length > 0) {
          setSelectedGoalId(response.data.data[0].id);
        }
        console.log('Fetched goals:', response.data.data); // Debug log
      }
    } catch (e) {
      setError('Failed to fetch savings goals');
    } finally {
      setIsLoading(false);
    }
  };

  // Always fetch goals on mount and when token changes
  useEffect(() => {
    if (token) {
      fetchGoals();
    }
  }, [token]);

  // Fetch transactions for a goal
  const fetchGoalTransactions = async (goalId: number) => {
    try {
      const response = await axiosInstance.get(`/savings-goals/${goalId}/transactions`);
      if (response.status === 200 && response.data.data) {
        setTransactions(response.data.data);
        console.log('Fetched transactions for goal', goalId, response.data.data);
      }
    } catch (e) {
      setError('Failed to fetch transactions');
    }
  };

  // Fetch transactions when selectedGoalId changes
  useEffect(() => {
    if (selectedGoalId) {
      fetchGoalTransactions(selectedGoalId);
    }
  }, [selectedGoalId]);

  // Helper function to get savings balance
  const getSavingsBalance = () => {
    console.log('Dashboard Data:', dashboardData);
    console.log('Accounts:', dashboardData?.accounts);
    const savingsAccount = dashboardData?.accounts?.find(
      account => account.accountType.toUpperCase() === 'SAVINGS'
    );
    console.log('Found Savings Account:', savingsAccount);
    const balance = savingsAccount?.balance || 0;
    console.log('Savings Balance:', balance);
    return balance;
  };

  // Helper function to format currency
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true
    });
  };

  // Create the portfolio data
  const savingsPortfolio = {
    ...(portfolio || {
      totalChange: '2.8',
      totalChangeValue: '137.50',
      monthlyContribution: 500.00,
    categories: [],
    transactions: [],
      nextGoal: 'Europe Trip'
    }),
    totalValue: getSavingsBalance()
  };

  if (isLoading || isDashboardLoading || !dashboardData) {
    return (
      <div style={{
        minHeight: '100vh',
        background: branding.pageBackground,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, color: branding.textColor, marginBottom: 16 }}>Loading savings portfolio...</div>
          <div style={{ color: '#666' }}>Please wait while we fetch your savings information</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: branding.pageBackground,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px'
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: 32,
          maxWidth: 480,
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)'
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>💰</div>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#c62828' }}>Error Loading Savings</div>
          <div style={{ marginBottom: 24, color: '#666' }}>{error}</div>
          <button 
            onClick={() => {
              setError(null);
              fetchPortfolio();
            }}
            style={{
              padding: '12px 24px',
              background: branding.primaryColor,
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      fontFamily: branding.fontFamily,
      background: branding.pageBackground,
      padding: isMobile ? '24px 0' : 0,
      margin: 0,
    }}>
      <div style={{
        maxWidth: 900,
        margin: isMobile ? '0 auto' : '32px auto 0 auto',
        padding: '0 16px',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          marginBottom: 32,
          gap: isMobile ? 24 : 0,
        }}>
          <div>
            <h1 style={{
              color: branding.textColor,
              fontWeight: 700,
              fontSize: isMobile ? 24 : 28,
              margin: 0,
              marginBottom: 8,
            }}>Savings Portfolio</h1>
            <div style={{ fontSize: isMobile ? 15 : 16, color: '#666' }}>
              Track and manage your savings goals
            </div>
          </div>
          <div style={{
            display: 'flex',
            gap: isMobile ? 8 : 12,
            overflowX: 'auto',
            paddingBottom: 8,
            margin: '0 -16px',
            paddingLeft: 16,
            paddingRight: 16,
          }}>
            {['1m', '3m', '6m', '1y', 'All'].map(timeframe => (
              <button
                key={timeframe}
                onClick={() => setSelectedTimeframe(timeframe)}
                style={{
                  padding: '8px 16px',
                  borderRadius: 12,
                  border: '1.5px solid',
                  borderColor: selectedTimeframe === timeframe ? '#f39c12' : '#eee',
                  background: selectedTimeframe === timeframe ? '#f39c12' : 'transparent',
                  color: selectedTimeframe === timeframe ? '#fff' : '#666',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                }}
              >
                {timeframe}
              </button>
            ))}
          </div>
        </div>

        {/* Portfolio Summary Card */}
        {portfolio && (
          <div style={{
            background: '#f39c12',
            borderRadius: 20,
            padding: isMobile ? 24 : 32,
            color: '#fff',
            marginBottom: 24,
            boxShadow: '0 4px 24px rgba(243,156,18,0.15)',
          }}>
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: isMobile ? 'stretch' : 'flex-start',
              marginBottom: isMobile ? 16 : 24,
              gap: isMobile ? 16 : 0,
            }}>
              <div>
                <div style={{ fontSize: isMobile ? 14 : 16, opacity: 0.9, marginBottom: 8 }}>Total Savings</div>
                <div style={{ fontSize: isMobile ? 32 : 36, fontWeight: 700 }}>{portfolio.totalValue}</div>
              </div>
              <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
                <div style={{ fontSize: isMobile ? 14 : 16, opacity: 0.9, marginBottom: 8 }}>Monthly Growth</div>
                <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#fff' }}>
                  {portfolio.totalChange}
                  <span style={{ fontSize: isMobile ? 14 : 16, opacity: 0.9, marginLeft: 8 }}>
                    ({portfolio.totalChangeValue})
                  </span>
                </div>
              </div>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: 16,
              borderTop: '1px solid rgba(255,255,255,0.2)',
            }}>
              <div>
                <div style={{ fontSize: 14, opacity: 0.9 }}>Monthly Contribution</div>
                <div style={{ fontSize: isMobile ? 18 : 20, fontWeight: 600 }}>{portfolio.monthlyContribution}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 14, opacity: 0.9 }}>Next Goal</div>
                <div style={{ fontSize: isMobile ? 18 : 20, fontWeight: 600 }}>{portfolio.nextGoal}</div>
              </div>
            </div>
          </div>
        )}

        {/* Savings Goals Panels */}
        {goals.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 16px 0' }}>
            <h2 style={{ color: branding.textColor, fontWeight: 700, fontSize: isMobile ? 20 : 24, margin: 0 }}>Savings Goals</h2>
            <button
              style={{
                padding: '10px 24px',
                background: branding.primaryColor,
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontWeight: 600,
                fontSize: isMobile ? 14 : 16,
                cursor: 'pointer'
              }}
              onClick={() => setShowCreateGoalModal(true)}
            >
              Create Savings Goal
            </button>
          </div>
        )}
        {goals.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 24,
            marginBottom: 32,
          }}>
            {paginatedGoals.map(goal => {
              // Map icon string to icon component
              const iconMap = {
                piggybank: <FaPiggyBank />,
                beach: <FaUmbrellaBeach />,
                car: <FaCar />,
                home: <FaHome />,
                plane: <FaPlane />,
                gift: <FaGift />,
              };
              const iconKey = (goal.icon?.toLowerCase() || '') as keyof typeof iconMap;
              const icon = iconMap[iconKey] || <FaPiggyBank />;
              return (
                <div
                  key={goal.id}
                  style={{
                    background: '#fff',
                    borderRadius: 20,
                    padding: 24,
                    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                    position: 'relative',
                    borderLeft: `8px solid ${goal.color || '#1976d2'}`,
                  }}
                >
                  <div style={{ position: 'absolute', top: 24, left: 16, width: 40, height: 40, borderRadius: '50%', background: goal.color || '#1976d2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 22 }}>
                    {icon}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, marginLeft: 56 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 18 }}>{goal.name}</div>
                      <div style={{ color: '#666', fontSize: 14 }}>Progress: {goal.progress.toFixed(1)}%</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 700, fontSize: 20 }}>€{goal.currentAmount.toLocaleString()} / €{goal.targetAmount.toLocaleString()}</div>
                      <div style={{ color: '#666', fontSize: 14, fontWeight: 600 }}>{goal.monthlyContribution ? `€${goal.monthlyContribution}/mo` : '€0/mo'}</div>
                    </div>
                  </div>
                  <div style={{ width: '100%', height: 8, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
                    <div style={{
                      width: `${goal.progress}%`,
                      height: '100%',
                      background: goal.color || '#1976d2',
                      borderRadius: 4,
                      transition: 'width 0.3s ease',
                    }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <button
                      style={{
                        padding: '4px 12px',
                        background: goal.color || '#1976d2',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        setTopUpGoal(goal);
                        setShowTopUpModal(true);
                      }}
                    >
                      Top Up
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Pagination Controls */}
        {goals.length > goalsPerPage && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 32, gap: 16 }}>
            <button
              onClick={() => setGoalsPage(goalsPage - 1)}
              disabled={goalsPage === 0}
              style={{ padding: '6px 16px', borderRadius: 8, border: 'none', background: goalsPage === 0 ? '#eee' : branding.primaryColor, color: goalsPage === 0 ? '#999' : '#fff', fontWeight: 600, fontSize: 18, cursor: goalsPage === 0 ? 'not-allowed' : 'pointer' }}
            >&lt;</button>
            <span style={{ fontWeight: 600, fontSize: 16 }}>Page {goalsPage + 1} of {totalGoalsPages}</span>
            <button
              onClick={() => setGoalsPage(goalsPage + 1)}
              disabled={goalsPage >= totalGoalsPages - 1}
              style={{ padding: '6px 16px', borderRadius: 8, border: 'none', background: goalsPage >= totalGoalsPages - 1 ? '#eee' : branding.primaryColor, color: goalsPage >= totalGoalsPages - 1 ? '#999' : '#fff', fontWeight: 600, fontSize: 18, cursor: goalsPage >= totalGoalsPages - 1 ? 'not-allowed' : 'pointer' }}
            >&gt;</button>
          </div>
        )}

        {/* Top Up Modal */}
        {showTopUpModal && topUpGoal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 32, minWidth: 320 }}>
              <h3>Top Up Goal: {topUpGoal.name}</h3>
              <div style={{ marginBottom: 12 }}>
                <label>Amount</label>
                <input
                  type="number"
                  value={topUpAmount}
                  onChange={e => setTopUpAmount(e.target.value)}
                  style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #eee', marginTop: 4 }}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label>From Account</label>
                <select
                  value={topUpAccountId}
                  onChange={e => setTopUpAccountId(e.target.value)}
                  style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #eee', marginTop: 4 }}
                >
                  <option value="">Select account</option>
                  {dashboardData?.accounts?.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.accountType} (****{account.accountNumber.slice(-4)})
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label>Description</label>
                <input
                  type="text"
                  value={topUpDescription}
                  onChange={e => setTopUpDescription(e.target.value)}
                  style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #eee', marginTop: 4 }}
                />
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowTopUpModal(false);
                    setTopUpGoal(null);
                    setTopUpAmount('');
                    setTopUpAccountId('');
                    setTopUpDescription('');
                  }}
                  style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#eee', color: '#333', fontWeight: 600 }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setIsToppingUp(true);
                    try {
                      await axiosInstance.post(`/savings-goals/${topUpGoal.id}/contribute`, {
                        amount: Number(topUpAmount),
                        fromAccountId: Number(topUpAccountId),
                        description: topUpDescription
                      });
                      setShowTopUpModal(false);
                      setTopUpGoal(null);
                      setTopUpAmount('');
                      setTopUpAccountId('');
                      setTopUpDescription('');
                      // Refresh goals
                      fetchGoals();
                      // Always fetch transactions for the topped-up goal
                      fetchGoalTransactions(topUpGoal.id);
                      fetchPortfolio();
                    } catch (e) {
                      alert('Failed to top up savings goal');
                    } finally {
                      setIsToppingUp(false);
                    }
                  }}
                  disabled={isToppingUp || !topUpAmount || !topUpAccountId}
                  style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: branding.primaryColor, color: '#fff', fontWeight: 600 }}
                >
                  {isToppingUp ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Transaction History */}
        {portfolio && (
          <div style={{
            background: '#fff',
            borderRadius: 20,
            padding: isMobile ? 24 : 32,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            marginBottom: 32,
          }}>
            <h3 style={{
              margin: 0,
              marginBottom: 24,
              color: branding.textColor,
              fontSize: isMobile ? 18 : 20,
              fontWeight: 600,
            }}>Transaction History</h3>
            {portfolio.transactions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 24px', color: '#666' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
                <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>No Transaction History</div>
                <div>Your savings transactions will appear here once you start saving.</div>
              </div>
            ) : (
              <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
                <table style={{ width: '100%', minWidth: 600, borderCollapse: 'collapse', fontSize: isMobile ? 14 : 16 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '12px 16px', color: '#666', fontWeight: 600, borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>Type</th>
                      <th style={{ textAlign: 'left', padding: '12px 16px', color: '#666', fontWeight: 600, borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>Category</th>
                      <th style={{ textAlign: 'left', padding: '12px 16px', color: '#666', fontWeight: 600, borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>Description</th>
                      <th style={{ textAlign: 'right', padding: '12px 16px', color: '#666', fontWeight: 600, borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>Amount</th>
                      <th style={{ textAlign: 'right', padding: '12px 16px', color: '#666', fontWeight: 600, borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {portfolio.transactions.map((tx) => (
                      <tr key={tx.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: 6,
                            fontSize: isMobile ? 13 : 14,
                            fontWeight: 600,
                            background: tx.type === 'Deposit' ? '#e8f5e8' : '#ffe8ec',
                            color: tx.type === 'Deposit' ? branding.successColorText : branding.errorColor,
                          }}>{tx.type}</span>
                        </td>
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 18 }}>{tx.icon}</span>
                            <span style={{ fontWeight: 600 }}>{tx.category}</span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', color: '#666', whiteSpace: 'nowrap' }}>{tx.description}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, color: tx.amount > 0 ? branding.successColorText : branding.errorColor, whiteSpace: 'nowrap' }}>
                          {tx.amount > 0 ? '+' : ''}€{Math.abs(tx.amount).toFixed(2)}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', color: '#666', whiteSpace: 'nowrap' }}>
                          {tx.date}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Contribute Modal */}
        {showContributeModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 32, minWidth: 320 }}>
              <h3>Add Savings to Goal</h3>
              <div style={{ marginBottom: 12 }}>
                <label>Amount</label>
                <input
                  type="number"
                  value={contributeAmount}
                  onChange={e => setContributeAmount(e.target.value)}
                  style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #eee', marginTop: 4 }}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label>Description</label>
                <input
                  type="text"
                  value={contributeDescription}
                  onChange={e => setContributeDescription(e.target.value)}
                  style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #eee', marginTop: 4 }}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label>From Account ID</label>
                <input
                  type="number"
                  value={contributeAccountId}
                  onChange={e => setContributeAccountId(e.target.value)}
                  style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #eee', marginTop: 4 }}
                />
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowContributeModal(false)}
                  style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#eee', color: '#333', fontWeight: 600 }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setIsContributing(true);
                    try {
                      await axiosInstance.post(`/savings-goals/${selectedGoalId}/contribute`, {
                        amount: Number(contributeAmount),
                        fromAccountId: Number(contributeAccountId),
                        description: contributeDescription
                      });
                      setShowContributeModal(false);
                      setContributeAmount('');
                      setContributeDescription('');
                      setContributeAccountId('');
                      // Refresh goals and transactions
                      fetchGoals();
                      if (selectedGoalId) fetchGoalTransactions(selectedGoalId);
                      fetchPortfolio();
                    } catch (e) {
                      alert('Failed to contribute to savings goal');
                    } finally {
                      setIsContributing(false);
                    }
                  }}
                  disabled={isContributing || !contributeAmount || !contributeAccountId}
                  style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: branding.primaryColor, color: '#fff', fontWeight: 600 }}
                >
                  {isContributing ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Savings Goal Modal */}
        {showCreateGoalModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 32, minWidth: 320 }}>
              <h3>Create Savings Goal</h3>
              <div style={{ marginBottom: 12 }}>
                <label>Name</label>
                <input
                  type="text"
                  value={newGoalName}
                  onChange={e => setNewGoalName(e.target.value)}
                  style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #eee', marginTop: 4 }}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label>Target Amount</label>
                <input
                  type="number"
                  value={newGoalTarget}
                  onChange={e => setNewGoalTarget(e.target.value)}
                  style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #eee', marginTop: 4 }}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label>Color</label>
                <input
                  type="color"
                  value={newGoalColor}
                  onChange={e => setNewGoalColor(e.target.value)}
                  style={{ width: 48, height: 32, border: 'none', background: 'none', marginLeft: 8, verticalAlign: 'middle' }}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label>Icon</label>
                <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                  {Object.entries({
                    piggybank: <FaPiggyBank />, 
                    beach: <FaUmbrellaBeach />, 
                    car: <FaCar />, 
                    home: <FaHome />, 
                    plane: <FaPlane />, 
                    gift: <FaGift />
                  }).map(([key, icon]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setNewGoalIcon(key)}
                      style={{
                        border: newGoalIcon === key ? `2px solid ${newGoalColor}` : '2px solid #eee',
                        background: newGoalIcon === key ? newGoalColor : '#fff',
                        color: newGoalIcon === key ? '#fff' : '#333',
                        borderRadius: '50%',
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 22,
                        cursor: 'pointer',
                        outline: 'none',
                        transition: 'all 0.2s',
                      }}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowCreateGoalModal(false)}
                  style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#eee', color: '#333', fontWeight: 600 }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setIsCreatingGoal(true);
                    try {
                      await axiosInstance.post('/savings-goals', {
                        name: newGoalName,
                        targetAmount: Number(newGoalTarget),
                        color: newGoalColor,
                        icon: newGoalIcon
                      });
                      setShowCreateGoalModal(false);
                      setNewGoalName('');
                      setNewGoalTarget('');
                      setNewGoalColor('#1976d2');
                      setNewGoalIcon('piggybank');
                      fetchGoals();
                    } catch (e) {
                      alert('Failed to create savings goal');
                    } finally {
                      setIsCreatingGoal(false);
                    }
                  }}
                  disabled={isCreatingGoal || !newGoalName || !newGoalTarget}
                  style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: branding.primaryColor, color: '#fff', fontWeight: 600 }}
                >
                  {isCreatingGoal ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Savings; 